import { PropsWithChildren } from 'react';
import { useToggle } from 'react-use';
import { Button, CustomButtonProps, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@ds';

type SchedulerConfirmationModalProps = PropsWithChildren<{
  title: string;
  isOpen: boolean;
  confirmText?: string;
  discardText?: string;
  confirmButtonProps?: CustomButtonProps;
  onConfirm: () => Promise<unknown>;
  onClose: () => void;
}>;

export const SchedulerConfirmationModal = ({
  title,
  children,
  isOpen,
  confirmText = 'Confirm',
  discardText = 'Discard',
  confirmButtonProps,
  onConfirm,
  onClose,
}: SchedulerConfirmationModalProps) => {
  const [isLoading, toggleLoading] = useToggle(false);

  const handleConfirm = async () => {
    toggleLoading(true);
    await onConfirm();
    toggleLoading(false);
    onClose();
  };

  return (
    <Modal
      size="xl"
      isOpen={isOpen}
      onClose={onClose}
      backdrop="opaque"
      hideCloseButton
      classNames={{
        base: 'px-6',
        body: 'border-y-1 border-base-50 px-0 py-6 text-l-r',
        header: 'px-0 font-secondary text-hl! text-base-20',
        footer: 'px-0 flex justify-between gap-2',
      }}
    >
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalBody>{children}</ModalBody>
        <ModalFooter>
          <Button size="md" variant="light" onClick={onClose}>
            {discardText}
          </Button>
          <Button
            size="md"
            color="default"
            variant="solid"
            isLoading={isLoading}
            onClick={handleConfirm}
            {...confirmButtonProps}
          >
            {confirmText}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
